import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DevicePhoneMobileIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { API_URLS } from '../config/api';

function PhoneVerification() {
  const [verificationCode, setVerificationCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [showChangePhone, setShowChangePhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [currentPhone, setCurrentPhone] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get registration data from navigation state
  const registrationData = location.state;
  
  // Determine the API URL on component mount
  useEffect(() => {
    const hostname = window.location.hostname;
    let baseUrl;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      baseUrl = API_URLS.default;
    } else {
      baseUrl = API_URLS.default;
    }
    
    console.log('Using API URL:', baseUrl);
    setApiUrl(baseUrl);
  }, []);
  
  // Redirect if no registration data
  useEffect(() => {
    if (!registrationData || !registrationData.userId) {
      navigate('/register');
    } else {
      setCurrentPhone(registrationData.phone || '');
    }
  }, [registrationData, navigate]);
  
  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (!apiUrl) {
      setError('API URL not configured. Please try again.');
      setLoading(false);
      return;
    }

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit verification code.');
      setLoading(false);
      return;
    }

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiUrl}/api/auth/verify-phone`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 15000;
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            setMessage(data.message);
            
            // Navigate to success page or login
            setTimeout(() => {
              navigate('/verification-success', {
                state: {
                  message: data.message,
                  email: data.email,
                  requiresEmailVerification: data.requiresEmailVerification
                }
              });
            }, 1500);
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
            setError('Invalid response from server');
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            setError(errorData.message || 'Failed to verify phone number');
          } catch (parseError) {
            setError(`Server error: ${xhr.status}`);
          }
        }
        setLoading(false);
      };
      
      xhr.onerror = function() {
        console.error('XHR error:', xhr.statusText);
        setError('Network error. Please check your connection and try again.');
        setLoading(false);
      };
      
      xhr.ontimeout = function() {
        setError('Request timed out. Please try again.');
        setLoading(false);
      };
      
      xhr.send(JSON.stringify({
        userId: registrationData.userId,
        verificationCode: verificationCode
      }));
    } catch (err) {
      console.error('Phone verification error:', err);
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setResending(true);
    setError('');
    setMessage('');

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiUrl}/api/auth/resend-phone-verification`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 15000;
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            setMessage(data.message);
            setCountdown(60); // 60 second cooldown
          } catch (parseError) {
            setError('Invalid response from server');
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            setError(errorData.message || 'Failed to resend verification code');
          } catch (parseError) {
            setError(`Server error: ${xhr.status}`);
          }
        }
        setResending(false);
      };
      
      xhr.onerror = function() {
        setError('Network error. Please check your connection and try again.');
        setResending(false);
      };
      
      xhr.ontimeout = function() {
        setError('Request timed out. Please try again.');
        setResending(false);
      };
      
      xhr.send(JSON.stringify({
        userId: registrationData.userId
      }));
    } catch (err) {
      console.error('Resend verification error:', err);
      setError(err.message || 'An unexpected error occurred');
      setResending(false);
    }
  };

  const handleUpdatePhone = async (e) => {
    e.preventDefault();
    setUpdatingPhone(true);
    setError('');
    setMessage('');

    if (!newPhone || newPhone.length < 10) {
      setError('Please enter a valid phone number.');
      setUpdatingPhone(false);
      return;
    }

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiUrl}/api/auth/update-phone-number`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 15000;
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            setMessage(data.message);
            setCurrentPhone(data.phone);
            setShowChangePhone(false);
            setNewPhone('');
            setVerificationCode(''); // Clear any existing code
            setCountdown(0); // Reset resend countdown
          } catch (parseError) {
            setError('Invalid response from server');
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            setError(errorData.message || 'Failed to update phone number');
          } catch (parseError) {
            setError(`Server error: ${xhr.status}`);
          }
        }
        setUpdatingPhone(false);
      };
      
      xhr.onerror = function() {
        setError('Network error. Please check your connection and try again.');
        setUpdatingPhone(false);
      };
      
      xhr.ontimeout = function() {
        setError('Request timed out. Please try again.');
        setUpdatingPhone(false);
      };
      
      xhr.send(JSON.stringify({
        userId: registrationData.userId,
        newPhone: newPhone
      }));
    } catch (err) {
      console.error('Update phone error:', err);
      setError(err.message || 'An unexpected error occurred');
      setUpdatingPhone(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setVerificationCode(value);
    }
  };

  if (!registrationData) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      <div className="container mx-auto px-4 h-full">
        <div className="flex content-center items-center justify-center min-h-screen py-12">
          <div className="w-full lg:w-5/12 px-4">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-2xl bg-white border-0">
              <div className="rounded-t-2xl mb-0 px-8 pt-8">
                <div className="text-center mb-3">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-indigo-100 mb-4">
                    <DevicePhoneMobileIcon className="h-8 w-8 text-indigo-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Verify Your Phone
                  </h1>
                  <p className="text-gray-600 text-sm mb-4">
                    We've sent a 6-digit verification code to
                  </p>
                  <div className="flex items-center justify-center mb-4">
                    <p className="text-indigo-600 font-semibold text-sm">
                      {currentPhone}
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowChangePhone(true)}
                      className="ml-2 text-xs text-gray-500 hover:text-indigo-600 underline"
                    >
                      Change
                    </button>
                  </div>
                  <p className="text-gray-500 text-xs">
                    Enter the code below to verify your phone number
                  </p>
                </div>
              </div>

              <div className="flex-auto px-8 pb-8 pt-0">
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-center">
                    {error}
                  </div>
                )}

                {message && (
                  <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl text-center">
                    {message}
                  </div>
                )}

                {showChangePhone && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Change Phone Number</h3>
                    <form onSubmit={handleUpdatePhone}>
                      <div className="mb-4">
                        <label htmlFor="newPhone" className="block text-sm font-medium text-gray-700 mb-1">
                          New Phone Number
                        </label>
                        <input
                          id="newPhone"
                          name="newPhone"
                          type="tel"
                          required
                          className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          value={newPhone}
                          onChange={(e) => setNewPhone(e.target.value)}
                          placeholder="Enter new phone number"
                        />
                      </div>
                      <div className="flex space-x-3">
                        <button
                          type="submit"
                          disabled={updatingPhone || !newPhone}
                          className="flex-1 py-2 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                          {updatingPhone ? 'Updating...' : 'Update Phone'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowChangePhone(false);
                            setNewPhone('');
                          }}
                          className="flex-1 py-2 px-4 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="relative">
                    <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-1">
                      Verification Code
                    </label>
                    <div className="relative">
                      <input
                        id="verificationCode"
                        name="verificationCode"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        maxLength="6"
                        className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-center text-2xl font-mono tracking-widest"
                        value={verificationCode}
                        onChange={handleChange}
                        placeholder="000000"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Code expires in 10 minutes</p>
                  </div>

                  <div className="text-center mt-6">
                    <button
                      type="submit"
                      disabled={loading || verificationCode.length !== 6}
                      className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all duration-200"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Verifying...
                        </div>
                      ) : (
                        'Verify Phone Number'
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center mt-6">
                  <p className="text-sm text-gray-600">
                    Didn't receive the code?{' '}
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resending || countdown > 0}
                      className="font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
                    >
                      {resending ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                    </button>
                  </p>
                </div>

                <div className="text-center mt-6">
                  <Link 
                    to="/register" 
                    className="inline-flex items-center text-sm text-gray-600 hover:text-indigo-600 font-medium"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back to registration
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhoneVerification; 